<script setup>
import { computed, defineProps } from "vue";
import moment from "moment";
import { announcementCategoryLabel } from "@/helpers/constants";

const props = defineProps({
  announcement: {
    type: Object,
    required: true,
  },
  // Compact drops the description to a clamped block — used inside the review queue,
  // where the announcement shares the screen with the record being worked on.
  compact: {
    type: Boolean,
    default: false,
  },
});

const isCameraCategory = computed(() => props.announcement.category === "camera");

const formattedDate = (date) => moment(date).format("ddd, D MMM YYYY hh:mm A");

const postedAt = computed(() => formattedDate(props.announcement.created_at));

const duration = computed(() => {
  const { starts_at, ends_at } = props.announcement;

  if (!starts_at && !ends_at) return null;

  const from = starts_at ? formattedDate(starts_at) : "Immediately";
  const to = ends_at ? formattedDate(ends_at) : "No end date";

  return `${from} → ${to}`;
});

const endsIn = computed(() => {
  if (!props.announcement.ends_at) return null;

  return moment(props.announcement.ends_at).fromNow();
});

// Not in effect yet — the active list includes these so they aren't invisible
// between creation and their start date.
const isScheduled = computed(
  () =>
    props.announcement.starts_at &&
    moment(props.announcement.starts_at).isAfter(moment())
);

const cameras = computed(() => props.announcement.cameras || []);
</script>

<template>
  <div class="announcement-details">
    <div class="d-flex flex-wrap align-items-center gap-2 mb-1">
      <h5 :class="compact ? 'fs-6 mb-0' : 'mb-0'">
        {{ announcement.title }}
      </h5>
      <b-badge :variant="isCameraCategory ? 'info' : 'secondary'">
        {{ announcementCategoryLabel(announcement.category) }}
      </b-badge>
      <b-badge v-if="isScheduled" variant="primary"> Scheduled </b-badge>
      <b-badge v-else-if="endsIn" variant="warning">
        Ends {{ endsIn }}
      </b-badge>
    </div>

    <div class="text-muted small mb-2">
      Posted {{ postedAt }}
      <span v-if="duration"> &middot; {{ duration }}</span>
    </div>

    <div v-if="isCameraCategory && cameras.length" class="mb-2">
      <b-badge
        v-for="camera in cameras"
        :key="camera.id"
        variant="light"
        class="text-dark border me-1"
        :title="camera.location"
      >
        <i class="mdi mdi-cctv me-1"></i>{{ camera.camera_id }}
      </b-badge>
    </div>

    <p :class="compact ? 'mb-0 small announcement-description' : 'mb-0'">
      {{ announcement.description }}
    </p>
  </div>
</template>

<style scoped>
.announcement-description {
  white-space: pre-line;
  max-height: 8rem;
  overflow-y: auto;
}
</style>
